import React, { useState, useEffect } from 'react';
import { useAuth, useTheme } from '@/lib/api';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Moon, Sun, User, Shield, Bell, BellOff, Palette, Phone, Download, Copy, ExternalLink, CheckCircle, Volume2, VolumeX, Pencil, RefreshCw, ChevronDown, AlertCircle, Lock, Chrome } from 'lucide-react';
import EditProfileDialog from '@/components/EditProfileDialog';
import PushNotificationsStatusCard from '@/components/PushNotificationsStatusCard';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { getPushState, setPushEnabled, sendPushTest } from '@/lib/pushNotifications';
import logger from '@/lib/logger';

function getRoleBadgeColor(role) {
    const colors = {
        super_admin: 'bg-purple-600',
        admin: 'bg-indigo-600',
        sales_manager: 'bg-blue-600',
        team_leader: 'bg-cyan-600',
        sales_executive: 'bg-green-600',
        cs_head: 'bg-orange-600',
        cs_agent: 'bg-amber-600',
        mentor: 'bg-pink-600',
        academic_master: 'bg-rose-600',
        finance: 'bg-emerald-600'
    };
    return colors[role] || 'bg-slate-600';
}

function formatRole(role) {
    if (!role) return 'Unknown';
    return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

function AdditionalPhonesList({ phones }) {
    if (!phones || phones.length === 0) return null;
    return (
        <div className="mt-2">
            <span className="text-sm text-muted-foreground">Additional Numbers:</span>
            <div className="flex flex-wrap gap-2 mt-1">
                {phones.map((phone, idx) => (
                    <Badge key={`phone-${idx}`} variant="outline">{phone}</Badge>
                ))}
            </div>
        </div>
    );
}

function ProfileCard({ user, onEdit }) {
    return (
        <Card>
            <CardHeader>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <User className="h-5 w-5" />
                        <CardTitle>Profile</CardTitle>
                    </div>
                    <Button variant="outline" size="sm" onClick={onEdit} data-testid="edit-profile-btn">
                        <Pencil className="h-4 w-4 mr-2" />Edit Profile
                    </Button>
                </div>
                <CardDescription>Your account information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                    {user?.profile_photo_url ? (
                        <img src={user.profile_photo_url} alt={user.full_name} className="w-20 h-20 rounded-full object-cover" />
                    ) : (
                        <div className="w-20 h-20 rounded-full bg-blue-600 flex items-center justify-center text-white text-3xl font-bold">
                            {user?.full_name?.charAt(0) || 'U'}
                        </div>
                    )}
                    <div>
                        <h3 className="text-xl font-semibold">{user?.full_name}</h3>
                        <p className="text-muted-foreground">{user?.email}</p>
                        <Badge className={getRoleBadgeColor(user?.role) + ' text-white mt-2'}>{formatRole(user?.role)}</Badge>
                    </div>
                </div>
                {user?.bio && <p className="text-sm text-muted-foreground italic">{user.bio}</p>}
                <Separator />
                <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-muted-foreground">Department:</span><span className="ml-2">{user?.department || 'Not assigned'}</span></div>
                    <div><span className="text-muted-foreground">Region:</span><span className="ml-2">{user?.region || 'Not assigned'}</span></div>
                    <div><span className="text-muted-foreground">Primary Phone:</span><span className="ml-2">{user?.phone || 'Not provided'}</span></div>
                    <div><span className="text-muted-foreground">Status:</span><Badge className="ml-2 bg-emerald-500 text-white">Active</Badge></div>
                </div>
                <AdditionalPhonesList phones={user?.additional_phones} />
            </CardContent>
        </Card>
    );
}

function ThemeCard({ theme, toggleTheme }) {
    return (
        <Card>
            <CardHeader>
                <div className="flex items-center gap-2"><Palette className="h-5 w-5" /><CardTitle>Appearance</CardTitle></div>
                <CardDescription>Customize your visual experience</CardDescription>
            </CardHeader>
            <CardContent>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        {theme === 'dark' ? <Moon className="h-5 w-5 text-blue-400" /> : <Sun className="h-5 w-5 text-amber-500" />}
                        <div>
                            <p className="font-medium">{theme === 'dark' ? 'Dark' : 'Light'} Mode</p>
                            <p className="text-sm text-muted-foreground">Toggle between light and dark themes</p>
                        </div>
                    </div>
                    <Switch checked={theme === 'dark'} onCheckedChange={toggleTheme} data-testid="theme-toggle" />
                </div>
            </CardContent>
        </Card>
    );
}

function NotificationsCard({
    soundEnabled, onToggleSound, savingSound,
    pushState, pushBusy, onTogglePush, onTestPush, onReEnablePush,
}) {
    // Why is the toggle OFF? Pick the right copy + which CTA to show.
    // - 'denied'         → browser-level block, user must unblock in browser
    // - 'user-disabled'  → user flipped the toggle off themselves
    // - 'no-subscription'→ permission granted but the SW lost its subscription
    //                      (cleared site data, switched devices, etc.)
    // - 'default'        → has never been asked yet (or dismissed the prompt)
    let offReason = null;
    if (pushState.supported && !pushState.active) {
        if (pushState.permission === 'denied') offReason = 'denied';
        else if (pushState.userDisabled) offReason = 'user-disabled';
        else if (pushState.permission === 'granted' && !pushState.subscribed) offReason = 'no-subscription';
        else offReason = 'default';
    }

    const reasonCopy = {
        denied: {
            tone: 'destructive',
            icon: Lock,
            title: 'Notifications are blocked by your browser',
            body: 'You (or someone using this browser) clicked "Block" when the ERP asked for permission. The toggle stays off until you unblock it in your browser settings — see the steps below.',
        },
        'user-disabled': {
            tone: 'amber',
            icon: BellOff,
            title: 'You turned notifications off',
            body: 'Click the button below to turn them back on. Your browser will ask for permission again.',
        },
        'no-subscription': {
            tone: 'amber',
            icon: AlertCircle,
            title: 'Notifications need to be re-registered',
            body: 'Permission is allowed but the subscription was lost (this happens after clearing site data, switching profiles, or signing in on a new device). Click below to re-register.',
        },
        default: {
            tone: 'sky',
            icon: Bell,
            title: 'Notifications are not enabled yet',
            body: 'Turn them on to receive desktop alerts for chat messages, HR approvals and other ERP events — even when this tab is in the background.',
        },
    };

    const r = offReason ? reasonCopy[offReason] : null;
    const toneClass = {
        destructive: 'border-red-500/40 bg-red-500/5 text-red-700 dark:text-red-300',
        amber:       'border-amber-500/40 bg-amber-500/5 text-amber-700 dark:text-amber-300',
        sky:         'border-sky-500/40 bg-sky-500/5 text-sky-700 dark:text-sky-300',
    };

    return (
        <Card>
            <CardHeader>
                <div className="flex items-center gap-2"><Bell className="h-5 w-5" /><CardTitle>Notifications</CardTitle></div>
                <CardDescription>Manage your notification preferences</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {/* Desktop push */}
                {pushState.supported && (
                    <>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <Bell className={`h-5 w-5 ${pushState.active ? 'text-green-500' : 'text-muted-foreground'}`} />
                                <div>
                                    <p className="font-medium">Desktop Push Notifications</p>
                                    <p className="text-sm text-muted-foreground">
                                        Get a desktop popup when someone messages you — works even when the ERP tab isn&apos;t focused.
                                    </p>
                                </div>
                            </div>
                            <Switch
                                checked={pushState.active}
                                onCheckedChange={onTogglePush}
                                disabled={pushBusy || pushState.permission === 'denied'}
                                data-testid="push-toggle"
                            />
                        </div>

                        {/* Reason banner — only shown when the toggle is OFF */}
                        {r && (
                            <div
                                className={`rounded-lg border px-4 py-3 ${toneClass[r.tone]}`}
                                data-testid={`push-off-reason-${offReason}`}
                            >
                                <div className="flex items-start gap-3">
                                    <r.icon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-sm">{r.title}</p>
                                        <p className="text-xs mt-1 text-foreground/80 leading-relaxed">{r.body}</p>
                                        {offReason !== 'denied' && (
                                            <Button
                                                size="sm"
                                                onClick={onReEnablePush}
                                                disabled={pushBusy}
                                                className="mt-3 bg-blue-600 hover:bg-blue-700 text-white"
                                                data-testid="push-reenable-btn"
                                            >
                                                {pushBusy ? (
                                                    <><RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Working…</>
                                                ) : (
                                                    <><Bell className="h-3.5 w-3.5 mr-1.5" /> Re-enable Notifications</>
                                                )}
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Browser-unblock guide — only shown when permission is denied */}
                        {offReason === 'denied' && <UnblockGuide />}

                        {pushState.active && (
                            <div className="pl-9">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={onTestPush}
                                    disabled={pushBusy}
                                    data-testid="push-test-btn"
                                >
                                    Send a test notification
                                </Button>
                            </div>
                        )}
                        <Separator />
                    </>
                )}

                {/* In-app sound */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        {soundEnabled ? <Volume2 className="h-5 w-5 text-green-500" /> : <VolumeX className="h-5 w-5 text-muted-foreground" />}
                        <div>
                            <p className="font-medium">Notification Sounds</p>
                            <p className="text-sm text-muted-foreground">Play sound alerts for new notifications</p>
                        </div>
                    </div>
                    <Switch checked={soundEnabled} onCheckedChange={onToggleSound} disabled={savingSound} data-testid="sound-toggle" />
                </div>
            </CardContent>
        </Card>
    );
}

// --- Browser unblock guide -------------------------------------------------
// Shown when the browser has permanently blocked notifications. Three
// browsers cover ~99% of CLT employees (Chrome / Edge / Firefox). Each is
// in its own Collapsible so the panel stays compact by default.
function UnblockGuide() {
    const [openId, setOpenId] = useState(() => {
        const ua = (typeof navigator !== 'undefined' ? navigator.userAgent : '').toLowerCase();
        if (ua.includes('edg/')) return 'edge';
        if (ua.includes('firefox')) return 'firefox';
        return 'chrome';
    });

    const browsers = [
        {
            id: 'chrome',
            label: 'Google Chrome',
            steps: [
                'Click the lock / tune icon on the left side of the address bar.',
                'Open "Site settings" (or directly click "Notifications").',
                'Change the Notifications dropdown from "Block" to "Allow".',
                'Come back to this tab and click Re-enable Notifications above. Refresh the page if the button stays off.',
            ],
        },
        {
            id: 'edge',
            label: 'Microsoft Edge',
            steps: [
                'Click the lock icon on the left side of the address bar.',
                'Click "Permissions for this site".',
                'Set Notifications to "Allow".',
                'Reload the ERP tab and click Re-enable Notifications above.',
            ],
        },
        {
            id: 'firefox',
            label: 'Mozilla Firefox',
            steps: [
                'Click the lock icon on the left side of the address bar.',
                'Open "More information" → "Permissions" (or use the small ⋯ menu next to Notifications).',
                'Uncheck "Use Default" and select "Allow" for "Send Notifications".',
                'Reload the ERP tab and click Re-enable Notifications above.',
            ],
        },
    ];

    return (
        <Alert className="border-dashed" data-testid="push-unblock-guide">
            <Chrome className="h-4 w-4" />
            <AlertTitle className="text-sm">How to unblock notifications</AlertTitle>
            <AlertDescription className="text-xs text-muted-foreground mt-1">
                Pick your browser below to see the exact steps. After unblocking, refresh this page and toggle on.
            </AlertDescription>
            <div className="mt-3 space-y-1.5">
                {browsers.map((b) => (
                    <Collapsible
                        key={b.id}
                        open={openId === b.id}
                        onOpenChange={(v) => setOpenId(v ? b.id : null)}
                    >
                        <CollapsibleTrigger
                            className="w-full flex items-center justify-between px-3 py-2 rounded-md border bg-background hover:bg-muted/40 text-sm font-medium transition-colors"
                            data-testid={`unblock-trigger-${b.id}`}
                        >
                            <span>{b.label}</span>
                            <ChevronDown className={`h-4 w-4 transition-transform ${openId === b.id ? 'rotate-180' : ''}`} />
                        </CollapsibleTrigger>
                        <CollapsibleContent
                            className="px-3 py-2.5 text-xs text-muted-foreground border border-t-0 rounded-b-md bg-muted/20"
                            data-testid={`unblock-steps-${b.id}`}
                        >
                            <ol className="list-decimal list-inside space-y-1.5">
                                {b.steps.map((step, idx) => <li key={idx}>{step}</li>)}
                            </ol>
                        </CollapsibleContent>
                    </Collapsible>
                ))}
            </div>
        </Alert>
    );
}

function IntegrationsCard({ templateData, loading3CX, onFetchTemplate, onDownload, onCopy }) {
    return (
        <Card>
            <CardHeader>
                <div className="flex items-center gap-2"><Phone className="h-5 w-5" /><CardTitle>3CX Integration</CardTitle></div>
                <CardDescription>Connect your phone system for click-to-call and call logging</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={onFetchTemplate} disabled={loading3CX}>
                        {loading3CX ? 'Loading...' : 'View Setup Instructions'}
                    </Button>
                    <Button variant="default" onClick={onDownload}><Download className="h-4 w-4 mr-2" />Download XML Template</Button>
                </div>
                {templateData && (
                    <div className="space-y-4 p-4 bg-muted rounded-lg">
                        <h4 className="font-medium">Setup Instructions:</h4>
                        {templateData.instructions && (
                            <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                                <li>{templateData.instructions.step1}</li>
                                <li>{templateData.instructions.step2}</li>
                                <li>{templateData.instructions.step3}</li>
                                <li>{templateData.instructions.step4}</li>
                                <li>{templateData.instructions.step5}</li>
                                <li>{templateData.instructions.step6}</li>
                                <li>{templateData.instructions.step7}</li>
                            </ol>
                        )}
                        <div className="pt-3 border-t">
                            <p className="text-sm font-medium mb-2">API Endpoints:</p>
                            <div className="space-y-1 text-xs font-mono bg-background p-2 rounded">
                                <p>Contact Lookup: {templateData.endpoints?.contact_lookup}</p>
                                <p>Call Journal: {templateData.endpoints?.call_journal}</p>
                            </div>
                        </div>
                        <a href="https://www.3cx.com/docs/crm-integration/" target="_blank" rel="noopener noreferrer" className="text-sm text-primary flex items-center gap-1 hover:underline">
                            <ExternalLink className="h-3 w-3" />View 3CX CRM Integration Guide
                        </a>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function SystemInfoCard() {
    return (
        <Card>
            <CardHeader><CardTitle>System Information</CardTitle><CardDescription>CLT Synapse System</CardDescription></CardHeader>
            <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="text-muted-foreground">Version:</span><span className="ml-2 font-mono">1.0.0</span></div>
                    <div><span className="text-muted-foreground">Environment:</span><Badge variant="outline" className="ml-2">Production</Badge></div>
                    <div><span className="text-muted-foreground">Timezone:</span><span className="ml-2">Asia/Dubai (GST)</span></div>
                    <div><span className="text-muted-foreground">Build:</span><span className="ml-2 font-mono">Phase 1</span></div>
                </div>
            </CardContent>
        </Card>
    );
}

const SettingsPage = () => {
    const { user, refreshUser } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const [loading3CX, setLoading3CX] = useState(false);
    const [templateData, setTemplateData] = useState(null);
    const [showEditProfile, setShowEditProfile] = useState(false);
    
    const [notificationSoundEnabled, setNotificationSoundEnabled] = useState(() => {
        const saved = localStorage.getItem('notificationSoundEnabled');
        return saved !== null ? JSON.parse(saved) : true;
    });
    const [savingSound, setSavingSound] = useState(false);

    // Desktop push notifications — ON by default for everyone. `active` is
    // true when permission is granted AND a live subscription exists AND
    // the user hasn't toggled it off here.
    const [pushState, setPushState] = useState({
        supported: false,
        permission: 'default',
        subscribed: false,
        userDisabled: false,
        active: false,
    });
    const [pushBusy, setPushBusy] = useState(false);

    const refreshPushState = async () => {
        const s = await getPushState();
        setPushState({
            ...s,
            active: s.supported && s.permission === 'granted' && s.subscribed && !s.userDisabled,
        });
    };

    useEffect(() => {
        refreshPushState();
        // Re-check when the tab regains focus (e.g. user just changed
        // browser-level permission and came back).
        const onFocus = () => refreshPushState();
        window.addEventListener('focus', onFocus);
        return () => window.removeEventListener('focus', onFocus);
    }, []);

    const handleTogglePush = async (enable) => {
        setPushBusy(true);
        try {
            await setPushEnabled(enable);
            await refreshPushState();
            toast.success(enable ? 'Desktop notifications enabled' : 'Desktop notifications disabled');
        } catch (e) {
            toast.error(e?.message || 'Could not update notification setting');
        } finally {
            setPushBusy(false);
        }
    };

    const handleTestPush = async () => {
        setPushBusy(true);
        try {
            await sendPushTest();
            toast.success('Test notification sent — check your desktop');
        } catch (e) {
            toast.error('Failed to send test notification');
        } finally {
            setPushBusy(false);
        }
    };

    // Re-enable Notifications: re-triggers the browser permission prompt and
    // re-registers the push subscription on the server. Used by the
    // "stuck OFF" recovery banner in NotificationsCard.
    const handleReEnablePush = async () => {
        if (pushState.permission === 'denied') {
            // Browser-level block — we cannot prompt again, the user has to
            // unblock manually first. Surface the guide below.
            toast.error('Notifications are blocked by your browser. Follow the steps below to unblock first.');
            return;
        }
        setPushBusy(true);
        try {
            await setPushEnabled(true);
            await refreshPushState();
            toast.success('Desktop notifications are back on');
        } catch (e) {
            // setPushEnabled throws "Notifications are blocked…" when the
            // permission prompt resolved as denied right now → refresh state
            // so the UI flips to the denied banner + unblock guide.
            await refreshPushState();
            toast.error(e?.message || 'Could not re-enable notifications');
        } finally {
            setPushBusy(false);
        }
    };

    const handleToggleSound = async (enabled) => {
        setSavingSound(true);
        setNotificationSoundEnabled(enabled);
        localStorage.setItem('notificationSoundEnabled', JSON.stringify(enabled));
        
        try {
            await api.put('/users/preferences', { notification_sound_enabled: enabled });
            toast.success(enabled ? 'Notification sounds enabled' : 'Notification sounds disabled');
        } catch (err) {
            logger.error('Failed to save:', err);
        } finally {
            setSavingSound(false);
        }
    };

    const fetch3CXTemplate = async () => {
        setLoading3CX(true);
        try {
            const response = await api.get('/3cx/template');
            setTemplateData(response.data);
        } catch (error) {
            toast.error('Failed to load 3CX template');
        } finally {
            setLoading3CX(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const download3CXTemplate = async () => {
        try {
            const response = await api.get('/3cx/template');
            const data = response.data;
            // Download as XML file for 3CX
            const blob = new Blob([data.template], { type: 'application/xml' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'CLT_Synapse_3CX_Template.xml';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            toast.success('3CX Template downloaded as XML');
        } catch (error) {
            toast.error('Failed to download template');
        }
    };

    return (
        <div className="space-y-6 max-w-4xl mx-auto" data-testid="settings-page">
            <div>
                <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
                <p className="text-muted-foreground">Manage your account and preferences</p>
            </div>

            <ProfileCard user={user} onEdit={() => setShowEditProfile(true)} />
            <ThemeCard theme={theme} toggleTheme={toggleTheme} />
            <NotificationsCard
                soundEnabled={notificationSoundEnabled}
                onToggleSound={handleToggleSound}
                savingSound={savingSound}
                pushState={pushState}
                pushBusy={pushBusy}
                onTogglePush={handleTogglePush}
                onTestPush={handleTestPush}
                onReEnablePush={handleReEnablePush}
            />
            <PushNotificationsStatusCard
                visible={['super_admin', 'admin', 'ceo'].includes(user?.role)}
            />
            <IntegrationsCard templateData={templateData} loading3CX={loading3CX} onFetchTemplate={fetch3CXTemplate} onDownload={download3CXTemplate} onCopy={copyToClipboard} />
            <SystemInfoCard />

            <EditProfileDialog 
                open={showEditProfile} 
                onOpenChange={setShowEditProfile}
                user={user}
                onSuccess={refreshUser}
            />
        </div>
    );
};

export default SettingsPage;
